import { useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QueryButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', query: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/queries`, formData);
      toast.success('Query submitted!', { description: 'We will get back to you soon.' });
      setFormData({ name: '', email: '', phone: '', query: '' });
      setIsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit query');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#C83041] hover:bg-[#A92332] text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 flex items-center justify-center"
        data-testid="query-floating-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom-4 duration-300" data-testid="query-modal">
            {/* Header */}
            <div className="bg-[#075FA3] px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white font-['Bricolage_Grotesque']">Have a Query?</h3>
                  <p className="text-white/80 text-sm">We&apos;d love to hear from you</p>
                </div>
                <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1" data-testid="query-close-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label htmlFor="q-name" className="text-sm font-medium text-[#231F20]">Name *</Label>
                <Input
                  id="q-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Your full name"
                  className="mt-1"
                  data-testid="query-name-input"
                />
              </div>
              <div>
                <Label htmlFor="q-email" className="text-sm font-medium text-[#231F20]">Email *</Label>
                <Input
                  id="q-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="your@email.com"
                  className="mt-1"
                  data-testid="query-email-input"
                />
              </div>
              <div>
                <Label htmlFor="q-phone" className="text-sm font-medium text-[#231F20]">Phone Number *</Label>
                <Input
                  id="q-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="+91 XXXXX XXXXX"
                  className="mt-1"
                  data-testid="query-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="q-query" className="text-sm font-medium text-[#231F20]">Your Query *</Label>
                <Textarea
                  id="q-query"
                  value={formData.query}
                  onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                  required
                  placeholder="What would you like to know?"
                  className="mt-1 min-h-[100px]"
                  data-testid="query-text-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !formData.name || !formData.email || !formData.phone || !formData.query}
                className="w-full bg-[#C83041] hover:bg-[#A92332] text-white rounded-full py-6 font-semibold"
                data-testid="query-submit-btn"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Submit Query</>}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
